/* CinematicShell — the render-ladder gate around every 3D scene.

   Contract:
   - Renders NOTHING in STATIC.  Callers should always have a static-safe
     fallback rendered outside this shell (typically the existing DOM copy).
   - Renders a plain <img> in LITE (path passed via `stillSrc`).  Zero JS
     cost — no three, no fiber, no gsap.
   - Renders `sceneImport()` (a lazy dynamic import) in CINEMATIC only.
     Because the import is inside useEffect, webpack code-splits every 3D
     scene into its own chunk that never enters the portals/admin bundles.

   Performance:
   - `IntersectionObserver` pauses the scene when it scrolls off screen.
   - `document.visibilitychange` pauses the scene when the tab is hidden.
   - A rolling FPS window dispatches `dt6:auto-downgrade` if the frame rate
     drops below 30 for 3 consecutive seconds.
*/
import { useEffect, useRef, useState } from "react";
import useRenderTier from "@/lib/cinematic/useRenderTier";

export default function CinematicShell({
  sceneImport,     // () => import("./scenes/HomeArsenal") — must default-export a component
  sceneProps,      // extra props forwarded to the scene component when it mounts
  stillSrc,        // string — LITE-tier still
  stillAlt = "",   // string — passed to <img alt> when LITE image is decorative, keep empty
  className = "",  // wrapper class
  style,           // inline styles for the wrapper (typically position/height)
  testId,          // data-testid for the wrapper
  children,        // fallback for STATIC (usually the DOM behind the scene)
}) {
  const tier = useRenderTier();
  const [SceneComp, setSceneComp] = useState(null);
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(true);

  // Lazy-import the 3D scene bundle only when we're on CINEMATIC.
  useEffect(() => {
    if (tier !== "cinematic" || !sceneImport) {
      setSceneComp(null);
      return;
    }
    let cancelled = false;
    sceneImport().then((mod) => {
      if (!cancelled) setSceneComp(() => mod.default || mod);
    }).catch(() => {
      // If the chunk fails to load, honestly downgrade to LITE.
      if (!cancelled) window.dispatchEvent(new CustomEvent("dt6:auto-downgrade"));
    });
    return () => { cancelled = true; };
  }, [tier, sceneImport]);

  // Off-viewport pause.
  useEffect(() => {
    if (tier !== "cinematic" || !wrapRef.current) return;
    const el = wrapRef.current;
    const io = new IntersectionObserver(
      (entries) => setVisible(entries[0].isIntersecting),
      { rootMargin: "80px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [tier]);

  // Tab-hidden pause + FPS watchdog.
  useEffect(() => {
    if (tier !== "cinematic") return;
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);

    // Auto-downgrade if FPS < 30 for 3s.
    let last = performance.now();
    let bad = 0;
    let raf;
    const tick = (now) => {
      const dt = now - last; last = now;
      const fps = 1000 / Math.max(dt, 1);
      if (fps < 30) bad += 1; else bad = 0;
      if (bad >= 60 * 3) { // ~3s worth of "bad" frames
        window.dispatchEvent(new CustomEvent("dt6:auto-downgrade"));
        try { console.info("[dt6] cinematic auto-downgrade fired (fps<30 for 3s)"); } catch (_e) {}
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [tier]);

  const wrapClass = `dt6-cinematic-shell ${className}`.trim();

  if (tier === "static") {
    return (
      <div ref={wrapRef} className={wrapClass} style={style} data-testid={testId} data-tier="static">
        {children}
      </div>
    );
  }

  if (tier === "lite") {
    return (
      <div ref={wrapRef} className={wrapClass} style={style} data-testid={testId} data-tier="lite">
        {stillSrc ? (
          <img
            src={stillSrc}
            alt={stillAlt}
            aria-hidden={stillAlt ? undefined : "true"}
            loading="lazy"
            decoding="async"
            className="dt6-cinematic-still"
          />
        ) : null}
        {children}
      </div>
    );
  }

  // CINEMATIC
  return (
    <div ref={wrapRef} className={wrapClass} style={style} data-testid={testId} data-tier="cinematic">
      {SceneComp ? <SceneComp paused={!visible} {...(sceneProps || {})} /> : (
        stillSrc ? (
          <img src={stillSrc} alt={stillAlt} aria-hidden="true" loading="eager" decoding="async" className="dt6-cinematic-still" />
        ) : null
      )}
      {children}
    </div>
  );
}
